/**
 * GraphQL queries and mutations for SMB signup and OTP authentication flows.
 *
 * This file centralizes all GraphQL operations for the NewCustomerSignupModal
 * component, following the codebase pattern of using gengql for type safety.
 */
import { gengql } from '@/__generated__';

// ============================================================================
// TOKEN & AUTHENTICATION
// ============================================================================

export const CREATE_TOKEN_MUTATION = gengql(/* GraphQL */ `
  mutation createToken(
    $userId: String
    $inviteToken: String
    $loginMeta: Json
    $recaptchaToken: String
  ) {
    createToken(
      user_id: $userId
      invite_token: $inviteToken
      login_meta: $loginMeta
      recaptcha_token: $recaptchaToken
      client_id: "node"
    ) {
      user_id
      invite_token
      login_meta
      token
    }
  }
`);

// ============================================================================
// OTP AUTHENTICATION
// ============================================================================

// Send OTP to phone/email for passwordless authentication
export const SEND_PASSWORDLESS_OTP_MUTATION = gengql(/* GraphQL */ `
  mutation sendPasswordlessOTP($address: String!, $transport: OTPTransport!) {
    sendPasswordlessOTP(address: $address, transport: $transport) {
      success
      maskedAddress
      expiresInSeconds
      userFound
      userName
      userCompany
    }
  }
`);

// Verify OTP code and get authentication result
// Note: user and billingAccount fields removed - use fetchViewer from @/core/viewer
// with the returned token to fetch user details for routing decisions.
export const VERIFY_PASSWORDLESS_OTP_MUTATION = gengql(/* GraphQL */ `
  mutation verifyPasswordlessOTP($address: String!, $code: String!, $transport: OTPTransport!) {
    verifyPasswordlessOTP(address: $address, code: $code, transport: $transport) {
      success
      phoneVerified
      otpVerificationToken
      token
      pendingUser {
        userId
        firstName
        lastName
        email
        companyName
        jobTitle
        hasStripeCustomer
      }
    }
  }
`);

// Link phone number to existing account (email conflict resolution)
// Note: user and billingAccount fields removed - use fetchViewer from @/core/viewer
// with the returned token to fetch user details for routing decisions.
export const LINK_PHONE_TO_EXISTING_ACCOUNT_MUTATION = gengql(/* GraphQL */ `
  mutation linkPhoneToExistingAccount($email: String!, $password: String!, $phone: String!) {
    linkPhoneToExistingAccount(email: $email, password: $password, phone: $phone) {
      success
      token
    }
  }
`);

// ============================================================================
// USER MANAGEMENT
// ============================================================================

// Create pending user at details form submission (early user creation)
export const CREATE_PENDING_USER_MUTATION = gengql(/* GraphQL */ `
  mutation createPendingUser($input: CreatePendingUserInput!) {
    createPendingUser(input: $input) {
      userId
    }
  }
`);

// Update pending user when navigating back and editing form
export const UPDATE_PENDING_USER_MUTATION = gengql(/* GraphQL */ `
  mutation updatePendingUser($input: UpdatePendingUserInput!) {
    updatePendingUser(input: $input) {
      userId
      emailChanged
    }
  }
`);

// ============================================================================
// SMB SIGNUP & BILLING
// ============================================================================

// Unified SMB SetupIntent mutation - creates Stripe SetupIntent for SMB signup
// Supports both OTP (pass otpVerificationToken) and Bearer token auth (omit token)
export const CREATE_SMB_SETUP_INTENT_MUTATION = gengql(/* GraphQL */ `
  mutation createSMBSetupIntent($input: SMBSetupIntentInput!) {
    createSMBSetupIntent(input: $input) {
      clientSecret
      stripeCustomerId
    }
  }
`);

// Unified SMB signup completion mutation
// Supports both OTP (pass otpVerificationToken) and Bearer token auth (omit token)
export const COMPLETE_SMB_SIGNUP_MUTATION = gengql(/* GraphQL */ `
  mutation completeSMBSignup($input: CompleteSMBSignupInput!) {
    completeSMBSignup(input: $input) {
      token
      user {
        id
        first_name
        last_name
        email {
          address
        }
      }
      team {
        id
        name
      }
      billingAccount {
        id
      }
    }
  }
`);

// ============================================================================
// SELF-SERVICE SMB DOMAIN MATCH
// ============================================================================

// Query the teams that accept a self-service signer's primary email domain.
// OTP-token-or-Bearer auth: the server resolves the user via the OTP claim
// (phone -> user), then derives the email domain from the user record.
// Result list is alphabetized server-side.
export const GROUPS_BY_EMAIL_DOMAIN_QUERY = gengql(/* GraphQL */ `
  query groupsByEmailDomain($otpVerificationToken: String) {
    groupsByEmailDomain(otpVerificationToken: $otpVerificationToken) {
      id
      name
      slug
    }
  }
`);

// Query the caller's pending project invite, matched server-side against
// their OTP/Bearer-verified primary email. Same OTP-token-or-Bearer auth
// as groupsByEmailDomain above. Returns null when there is no pending
// invite. Mints a fresh invite JWT (suitable for acceptInvitation) on
// every call, not the original token from the invite email.
export const PENDING_PROJECT_INVITE_QUERY = gengql(/* GraphQL */ `
  query pendingProjectInvite($otpVerificationToken: String) {
    pendingProjectInvite(otpVerificationToken: $otpVerificationToken) {
      id
      email
      role
      projectName
      inviterName
      token
    }
  }
`);

// Query whether the caller already holds an active project membership on
// an open project — the same signal completeSMBSignup's active-membership
// guard blocks on, exposed early (ENG-5334 follow-up) so the frontend can
// warn before card entry instead of only after. Same OTP-token-or-Bearer
// auth as pendingProjectInvite above.
export const ACTIVE_PROJECT_MEMBERSHIP_QUERY = gengql(/* GraphQL */ `
  query activeProjectMembership($otpVerificationToken: String) {
    activeProjectMembership(otpVerificationToken: $otpVerificationToken)
  }
`);

// Accepts a pending project invite using the freshly minted invite JWT
// returned by pendingProjectInvite (not the original invite-email token).
// Returns the URL to send the user to next, mirroring the acceptInvitation
// mutation the invite-email-link join flow already uses (actions/invitation.ts),
// or null. Distinct operation name (same underlying field) since this call
// site runs through authSession.createClient() with a freshly authenticated
// Bearer token, rather than the app-boot Apollo client the existing thunk rides.
export const ACCEPT_PROJECT_INVITATION_MUTATION = gengql(/* GraphQL */ `
  mutation acceptProjectInvitation($token: String!) {
    acceptInvitation(token: $token)
  }
`);

// File a join request against a domain-matched team. The team owner approves
// or rejects via the existing addGroupMember flow. Result row is normally in
// awaiting_approval state, but is auto-approved (active) when an existing
// email-only invitation matches the user's verified primary email. The
// response carries a Bearer token so the caller (a fresh OTP signup with no
// prior session) can navigate the app immediately.
export const REQUEST_TO_JOIN_GROUP_MUTATION = gengql(/* GraphQL */ `
  mutation requestToJoinGroup($groupId: String!, $otpVerificationToken: String) {
    requestToJoinGroup(groupId: $groupId, otpVerificationToken: $otpVerificationToken) {
      member {
        id
        role
        state
      }
      token
    }
  }
`);

// ============================================================================
// CONSULTATION
// ============================================================================

// Request consultation mutation - imports complete mutation from consultation store
// This includes all required parameters: expert_request_id, group_id, requester_id,
// credits, profile_type, disclosure, tracking_code
// See: src/consultation/store/queries.ts
export { REQUEST_CONSULTATION as REQUEST_CONSULTATION_MUTATION } from '@/consultation/store/queries';
